/**
 * FormulationDocument — el "documento vivo" del Asistente Formulador.
 *
 * Renderiza los 6 modulos de identificacion de la MGA en formato de
 * documento editable, seguidos (slice 2) de la seccion "Preparación" con
 * los 3 modulos de cadena de valor / presupuesto / cronograma — gateada
 * hasta que identificacion este 100% confirmada (ADR-021 regla 5). Cada
 * modulo tiene estado (empty/draft/confirmed), origen (empty/ai/edited) y
 * supuestos de la IA.
 *
 * Principio de diseno: flat, blanco, limpio. Sin gradientes. Honestidad
 * visual: el origen de cada modulo siempre es visible.
 *
 * Props:
 * - `formulation`: el DTO completo de la formulacion.
 * - `municipality`: DANE del municipio activo (para las mutations).
 * - `isPro`: si el usuario tiene plan Pro (gate del boton "Ayudame").
 * - `projectSector`: sector Kaptar del expediente (`project.sector`). Si es
 *   'generico', la seccion Preparación desbloqueada ofrece el selector de
 *   sector (slice 2) — se pasa desde `ExpedienteDetail`, que es quien tiene
 *   el `ProjectDetailDTO` cargado.
 */

import { useQuery } from "@tanstack/react-query";

import { fetchMunicipalityProfile } from "@/lib/api/municipalities";
import type { FormulationDTO } from "@/lib/api/formulations";
import {
  isIdentificacionConfirmada,
  MODULE_ORDER,
  PREPARACION_ORDER,
} from "@/lib/api/formulations";
import { useDraftModule, useUpdateModule } from "@/lib/hooks/useFormulations";

import { ModuleSection } from "./ModuleSection";
import { SectorSelector } from "./SectorSelector";

/** Cuanto tiempo consideramos "fresco" el perfil municipal antes de refetch. */
const MUNICIPALITY_PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Chips de contexto municipal — hacen VISIBLE el data-grounding del
 * documento ("Anclado en tu municipio" no es solo una etiqueta, es un
 * hecho verificable). Slice 1.1.
 *
 * Degradacion elegante: mientras carga o si falla, no rompe nada — el
 * label "Anclado en tu municipio" sigue mostrandose solo, sin spinner
 * ni mensaje de error (esto es decoracion de confianza, no un dato
 * critico que bloquee el flujo).
 */
function MunicipalContextChips({ municipality }: { municipality: string }) {
  const { data, isError } = useQuery({
    queryKey: ["municipality-profile", municipality],
    queryFn: () => fetchMunicipalityProfile(municipality),
    staleTime: MUNICIPALITY_PROFILE_STALE_TIME_MS,
    enabled: Boolean(municipality),
  });

  if (!data || isError) return null;

  const chipClass =
    "rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700";

  const mdm = data.indicators["mdm_score_total"];
  const ipm = data.indicators["ipm_total"];

  return (
    <div data-testid="municipal-context-chips" className="flex flex-wrap items-center gap-1.5">
      <span className={chipClass}>{data.name}</span>
      <span className={chipClass}>Cat. {data.category}</span>
      {data.is_pdet && <span className={chipClass}>PDET</span>}
      {data.population != null && (
        <span className={chipClass}>Pob. {data.population.toLocaleString("es-CO")}</span>
      )}
      {mdm && <span className={chipClass}>MDM {mdm.value}</span>}
      {ipm && <span className={chipClass}>IPM {ipm.value}%</span>}
    </div>
  );
}

interface FormulationDocumentProps {
  formulation: FormulationDTO;
  municipality: string;
  isPro: boolean;
  /** Sector Kaptar del expediente (slice 2 — gate del selector de sector). */
  projectSector: string;
}

/** Icono de escudo para el disclaimer honesto. */
function ShieldIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="h-4 w-4 shrink-0 text-cta-600"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

/** Cuantos de los 6 modulos de identificacion estan confirmados. */
function countConfirmed(formulation: FormulationDTO): number {
  return MODULE_ORDER.filter((key) => formulation.modules[key]?.status === "confirmed").length;
}

/**
 * Barra de progreso de los 6 modulos (cuantos confirmados).
 *
 * Un segmento por modulo y no una barra continua: el progreso aca es
 * discreto (6 pasos), y los segmentos dejan ver de un vistazo cuantos
 * faltan sin tener que leer el contador.
 *
 * El contenedor de los segmentos lleva `flex-1`. Sin eso colapsaba a ancho
 * cero -- los segmentos son `flex-1` (basis 0) y el contenedor, al ser a su
 * vez un flex item sin grow, se dimensionaba a su contenido, que era 0. La
 * barra existia en el DOM y no se veia en pantalla.
 */
function ConfirmedProgressBar({
  formulation,
  size = "sm",
}: {
  formulation: FormulationDTO;
  /** `md` engorda la barra para cuando es el elemento principal de la card. */
  size?: "sm" | "md";
}) {
  const confirmed = countConfirmed(formulation);
  const total = MODULE_ORDER.length;
  const heightClass = size === "md" ? "h-2.5" : "h-1.5";

  return (
    <div
      className="flex items-center gap-3"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={confirmed}
      aria-valuetext={`${confirmed} de ${total} módulos confirmados`}
    >
      <div className="flex flex-1 gap-1" aria-hidden="true">
        {MODULE_ORDER.map((key) => {
          const isConfirmed = formulation.modules[key]?.status === "confirmed";
          return (
            <div
              key={key}
              data-testid="progress-segment"
              data-confirmed={isConfirmed}
              className={`${heightClass} flex-1 rounded-full transition-colors duration-300 ${
                isConfirmed ? "bg-emerald-500" : "bg-slate-200"
              }`}
            />
          );
        })}
      </div>
      <span className="shrink-0 text-xs text-slate-600 tabular-nums">
        {confirmed} de {total} confirmados
      </span>
    </div>
  );
}

/** Icono de candado — estado bloqueado de la seccion Preparación. */
function LockIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      className="h-5 w-5 shrink-0 text-slate-400"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

/**
 * Card de la seccion Preparación mientras identificacion no esta 100%
 * confirmada (ADR-021 regla 5). Reusa `ConfirmedProgressBar` para la barra
 * X/6 — es exactamente el mismo conteo, ya calculado ahi.
 */
function PreparacionLockedCard({ formulation }: { formulation: FormulationDTO }) {
  const faltantes = MODULE_ORDER.length - countConfirmed(formulation);

  return (
    <div
      data-testid="preparacion-locked"
      className="flex flex-col gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5"
    >
      <div className="flex items-center gap-2">
        <LockIcon />
        <p className="text-sm font-medium text-slate-700">
          Confirma los 6 módulos de identificación para desbloquear la preparación.
        </p>
      </div>
      <ConfirmedProgressBar formulation={formulation} size="md" />
      {/*
        Cuanto falta, no cuanto se lleva. Con la barra ya mostrando el
        avance, el dato que mueve al usuario es el que queda por delante --
        y "falta 1" lee muy distinto de "5 de 6".
      */}
      <p data-testid="preparacion-faltantes" className="text-xs text-slate-500">
        {faltantes === 1
          ? "Falta 1 módulo por confirmar."
          : `Faltan ${faltantes} módulos por confirmar.`}
      </p>
    </div>
  );
}

/**
 * Mensaje legible del ultimo intento de `draft` fallido para `moduleKey`,
 * o `null` si no aplica. Cubre cualquier error que NO sea 402 (gate
 * Pro/cuota — ya se muestra arriba en el banner `draftBlockedReason`) ni
 * 409 (gate de preparacion incompleta — ya se muestra en
 * `preparacion-409-notice`). Esto incluye el 503 que el backend devuelve
 * cuando el modelo no logra completar el borrador ("no se desconto tu
 * cuota..."), 500s y errores de red — `ApiError.message` ya trae el texto
 * humano armado por `apiErrorFromResponse`.
 *
 * Se resuelve por modulo (no un banner global de documento) porque un
 * expediente puede tener hasta 9 modulos: el usuario puede haber
 * scrolleado lejos del header cuando el intento falla, y el mensaje debe
 * aparecer junto al boton que lo disparo.
 */
/**
 * True si hay un guardado en vuelo para ESTE modulo. Se resuelve por modulo
 * y no con el `isPending` global porque el documento puede tener hasta 9
 * `ModuleSection` montados: sin discriminar, confirmar uno pondria en
 * "Confirmando…" a los nueve.
 */
function isSavingModule(mutation: ReturnType<typeof useUpdateModule>, moduleKey: string): boolean {
  return mutation.isPending && mutation.variables?.moduleKey === moduleKey;
}

function draftErrorMessageFor(
  mutation: ReturnType<typeof useDraftModule>,
  moduleKey: string,
): string | null {
  if (!mutation.isError) return null;
  if (mutation.variables?.moduleKey !== moduleKey) return null;
  const err = mutation.error;
  if (!err || err.status === 402 || err.status === 409) return null;
  return err.message;
}

export function FormulationDocument({
  formulation,
  municipality,
  isPro,
  projectSector,
}: FormulationDocumentProps) {
  const draftMutation = useDraftModule(formulation.id, municipality, formulation.project_id);
  const updateMutation = useUpdateModule(formulation.id, municipality, formulation.project_id);

  // El error 402 del draft expone reason para el upsell futuro.
  // Por ahora lo dejamos en consola para que el dev lo vea facilmente.
  const draftError = draftMutation.error;
  const draftBlockedReason =
    draftError?.status === 402
      ? String(draftError.detailData?.reason ?? "tier_locked_pro_required")
      : null;

  // Slice 2 / ADR-021 regla 5: la seccion Preparación solo se desbloquea
  // cuando los 6 modulos de identificacion estan confirmados.
  const preparacionUnlocked = isIdentificacionConfirmada(formulation);

  // Gate 409 (`identificacion_incompleta`): el estado local decia
  // "desbloqueado" pero el backend lo rechazo (ej. un modulo se
  // des-confirmo en otra pestaña). Los hooks ya invalidan la formulacion
  // en `onError`; acá solo mostramos el aviso amable mientras llega el
  // refetch.
  const preparacionGateStale =
    draftMutation.error?.status === 409 || updateMutation.error?.status === 409;

  return (
    <section
      data-testid="formulation-document"
      aria-label="Documento de formulación"
      className="space-y-4"
    >
      {/* Header card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
        {/* Idea en cursiva como subtitulo */}
        <p className="mb-3 text-sm text-slate-500 italic">"{formulation.idea_text}"</p>

        {/* Etiqueta de contexto municipal + chips reales del perfil */}
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-brand-700 uppercase">
            Anclado en tu municipio
          </span>
          <MunicipalContextChips municipality={municipality} />
        </div>

        {/* Error de gate 402 al redactar */}
        {draftBlockedReason && (
          <div
            data-testid="draft-gate-error"
            className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            {draftBlockedReason === "ai_quota_exhausted"
              ? "Cuota IA agotada este mes. Se renueva el 1 del próximo mes."
              : "El Asistente Formulador requiere plan Pro."}
          </div>
        )}

        {/* Progreso de modulos confirmados */}
        <ConfirmedProgressBar formulation={formulation} />

        {/* Disclaimer */}
        <div className="mt-3 flex items-start gap-1.5">
          <ShieldIcon />
          <p className="text-xs text-slate-400">
            Borrador para acelerar tu formulación. No reemplaza la MGA oficial ni la revisión del
            OCAD.
          </p>
        </div>
      </div>

      {/* Los 6 modulos */}
      {MODULE_ORDER.map((key, idx) => {
        const mod = formulation.modules[key] ?? {
          key,
          content: "",
          status: "empty" as const,
          origin: "empty" as const,
          assumptions: [],
        };
        const isDrafting = draftMutation.isPending && draftMutation.variables?.moduleKey === key;

        return (
          <ModuleSection
            key={key}
            module={mod}
            index={idx + 1}
            isPro={isPro}
            isDrafting={isDrafting}
            isSaving={isSavingModule(updateMutation, key)}
            draftError={draftErrorMessageFor(draftMutation, key)}
            onDraft={() => draftMutation.mutate({ moduleKey: key })}
            onSave={(content, status) => updateMutation.mutate({ moduleKey: key, content, status })}
          />
        );
      })}

      {/* Preparación (slice 2, ADR-021) — gateada a identificacion confirmada */}
      <div data-testid="preparacion-section" className="space-y-4 pt-2">
        <h3 className="text-base font-semibold text-slate-900">Preparación</h3>

        {preparacionGateStale && (
          <div
            data-testid="preparacion-409-notice"
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            Confirma primero los 6 módulos de identificación.
          </div>
        )}

        {!preparacionUnlocked ? (
          <PreparacionLockedCard formulation={formulation} />
        ) : (
          <>
            {projectSector === "generico" && (
              <SectorSelector projectId={formulation.project_id} municipality={municipality} />
            )}

            {PREPARACION_ORDER.map((key, idx) => {
              const mod = formulation.modules[key] ?? {
                key,
                content: "",
                status: "empty" as const,
                origin: "empty" as const,
                assumptions: [],
              };
              const isDrafting =
                draftMutation.isPending && draftMutation.variables?.moduleKey === key;

              return (
                <div key={key} className="space-y-2">
                  {key === "presupuesto" && projectSector === "generico" && (
                    <p
                      data-testid="presupuesto-sector-hint"
                      className="px-1 text-xs text-slate-500"
                    >
                      Define el sector del proyecto arriba para obtener el rango de presupuesto de
                      referencia.
                    </p>
                  )}
                  <ModuleSection
                    module={mod}
                    index={MODULE_ORDER.length + idx + 1}
                    isPro={isPro}
                    isDrafting={isDrafting}
                    isSaving={isSavingModule(updateMutation, key)}
                    draftError={draftErrorMessageFor(draftMutation, key)}
                    onDraft={() => draftMutation.mutate({ moduleKey: key })}
                    onSave={(content, status) =>
                      updateMutation.mutate({ moduleKey: key, content, status })
                    }
                  />
                </div>
              );
            })}
          </>
        )}

        {/* Disclaimer reforzado ADR-021 (regla 6) */}
        <div className="flex items-start gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <ShieldIcon />
          <p className="text-xs text-slate-500">
            Estimación de referencia basada en históricos SGR; no es un presupuesto oficial ni
            reemplaza el análisis de tu equipo financiero.
          </p>
        </div>
      </div>
    </section>
  );
}
